import {
    DocumentCategory,
    DocumentFolder,
    DocumentType,
} from '../../core-models';

const DOCUMENT_FOLDER_BY_CATEGORY_VALUES = {
    [DocumentCategory.Other]: DocumentFolder.Other,
    [DocumentCategory.Asbestos]: DocumentFolder.HealthSafetyAndCompliance,
    [DocumentCategory.EmergencyResponsePack]:
        DocumentFolder.HealthSafetyAndCompliance,
    [DocumentCategory.Evacuation]: DocumentFolder.HealthSafetyAndCompliance,
    [DocumentCategory.FireSafetyCompliance]:
        DocumentFolder.HealthSafetyAndCompliance,
    [DocumentCategory.FireStrategy]: DocumentFolder.HealthSafetyAndCompliance,
    [DocumentCategory.HealthSafetyFiles]:
        DocumentFolder.HealthSafetyAndCompliance,
    [DocumentCategory.IncidentRiskReporting]:
        DocumentFolder.HealthSafetyAndCompliance,
    [DocumentCategory.PestControl]: DocumentFolder.HealthSafetyAndCompliance,
    [DocumentCategory.RiskAssessments]:
        DocumentFolder.HealthSafetyAndCompliance,
    [DocumentCategory.SpecificationsHealthSafetyAndCompliance]:
        DocumentFolder.HealthSafetyAndCompliance,
    [DocumentCategory.TrainingCompetenceHealthSafetyAndCompliance]:
        DocumentFolder.HealthSafetyAndCompliance,
    [DocumentCategory.BuildingApprovals]:
        DocumentFolder.ConstructionAndDevelopment,
    [DocumentCategory.ContractsConstructionAndDevelopment]:
        DocumentFolder.ConstructionAndDevelopment,
    [DocumentCategory.InspectionsReportsSurveysConstructionAndDevelopment]:
        DocumentFolder.ConstructionAndDevelopment,
    [DocumentCategory.Planning]: DocumentFolder.ConstructionAndDevelopment,
    [DocumentCategory.PracticalCompletionDefects]:
        DocumentFolder.ConstructionAndDevelopment,
    [DocumentCategory.ProductMaterialInformation]:
        DocumentFolder.ConstructionAndDevelopment,
    [DocumentCategory.ProjectInformation]:
        DocumentFolder.ConstructionAndDevelopment,
    [DocumentCategory.SpecificationsConstructionAndDevelopment]:
        DocumentFolder.ConstructionAndDevelopment,
    [DocumentCategory.Warranties]: DocumentFolder.ConstructionAndDevelopment,
    [DocumentCategory.CommunicationSystems]:
        DocumentFolder.BuildingOperationsAndFacilities,
    [DocumentCategory.ContractsBuildingOperationsAndFacilities]:
        DocumentFolder.BuildingOperationsAndFacilities,
    [DocumentCategory.ElectricalSystems]:
        DocumentFolder.BuildingOperationsAndFacilities,
    [DocumentCategory.Financial]:
        DocumentFolder.BuildingOperationsAndFacilities,
    [DocumentCategory.FireDoors]:
        DocumentFolder.BuildingOperationsAndFacilities,
    [DocumentCategory.GasSystems]:
        DocumentFolder.BuildingOperationsAndFacilities,
    [DocumentCategory.GreaseTrap]:
        DocumentFolder.BuildingOperationsAndFacilities,
    [DocumentCategory.EmbeddedNetworks]:
        DocumentFolder.BuildingOperationsAndFacilities,
    [DocumentCategory.InspectionsReportsSurveysBuildingOperationsAndFacilities]:
        DocumentFolder.BuildingOperationsAndFacilities,
    [DocumentCategory.LifeSafetySystems]:
        DocumentFolder.BuildingOperationsAndFacilities,
    [DocumentCategory.LiftsElevators]:
        DocumentFolder.BuildingOperationsAndFacilities,
    [DocumentCategory.OMManual]: DocumentFolder.BuildingOperationsAndFacilities,
    [DocumentCategory.OtherBuildingAssets]:
        DocumentFolder.BuildingOperationsAndFacilities,
    [DocumentCategory.Permits]: DocumentFolder.BuildingOperationsAndFacilities,
    [DocumentCategory.PoliciesProceduresStrategies]:
        DocumentFolder.BuildingOperationsAndFacilities,
    [DocumentCategory.PressureSystems]:
        DocumentFolder.BuildingOperationsAndFacilities,
    [DocumentCategory.SecuritySystems]:
        DocumentFolder.BuildingOperationsAndFacilities,
    [DocumentCategory.Staffing]: DocumentFolder.BuildingOperationsAndFacilities,
    [DocumentCategory.SuppliersInformation]:
        DocumentFolder.BuildingOperationsAndFacilities,
    [DocumentCategory.TrainingCompetenceBuildingOperationsAndFacilities]:
        DocumentFolder.BuildingOperationsAndFacilities,
    [DocumentCategory.Utilities]:
        DocumentFolder.BuildingOperationsAndFacilities,
    [DocumentCategory.VentilationSystems]:
        DocumentFolder.BuildingOperationsAndFacilities,
    [DocumentCategory.WaterSystems]:
        DocumentFolder.BuildingOperationsAndFacilities,
    [DocumentCategory.Architectural]: DocumentFolder.Drawings,
    [DocumentCategory.CivilStructural]: DocumentFolder.Drawings,
    [DocumentCategory.Mechanical]: DocumentFolder.Drawings,
    [DocumentCategory.Electrical]: DocumentFolder.Drawings,
    [DocumentCategory.FireLifeSafety]: DocumentFolder.Drawings,
    [DocumentCategory.Hydraulic]: DocumentFolder.Drawings,
    [DocumentCategory.Landscape]: DocumentFolder.Drawings,
    [DocumentCategory.FacadeCladding]: DocumentFolder.Drawings,
    [DocumentCategory.ComplianceCertificates]:
        DocumentFolder.EnvironmentalAndSite,
    [DocumentCategory.GroundGeotechnical]: DocumentFolder.EnvironmentalAndSite,
    [DocumentCategory.InspectionsReportsSurveysEnvironmentalAndSite]:
        DocumentFolder.EnvironmentalAndSite,
    [DocumentCategory.LicencesNotices]: DocumentFolder.EnvironmentalAndSite,
    [DocumentCategory.Waste]: DocumentFolder.EnvironmentalAndSite,
    [DocumentCategory.EasementsRightOfWay]: DocumentFolder.PropertyAndLegal,
    [DocumentCategory.HeadLease]: DocumentFolder.PropertyAndLegal,
    [DocumentCategory.Licences]: DocumentFolder.PropertyAndLegal,
    [DocumentCategory.Marketing]: DocumentFolder.PropertyAndLegal,
    [DocumentCategory.PartyWall]: DocumentFolder.PropertyAndLegal,
    [DocumentCategory.RentOutgoings]: DocumentFolder.PropertyAndLegal,
    [DocumentCategory.ResidentTenantEngagement]:
        DocumentFolder.PropertyAndLegal,
    [DocumentCategory.TenanciesUnits]: DocumentFolder.PropertyAndLegal,
    [DocumentCategory.Title]: DocumentFolder.PropertyAndLegal,
    [DocumentCategory.Transactions]: DocumentFolder.PropertyAndLegal,
    [DocumentCategory.InsuranceClaimsDisputes]:
        DocumentFolder.FinanceAndInsurance,
    [DocumentCategory.InsurancePoliciesCertificates]:
        DocumentFolder.FinanceAndInsurance,
    [DocumentCategory.Tax]: DocumentFolder.FinanceAndInsurance,
    [DocumentCategory.Valuations]: DocumentFolder.FinanceAndInsurance,
} as const satisfies Readonly<Record<DocumentCategory, DocumentFolder>>;

const DOCUMENT_CATEGORIES = Object.values(DocumentCategory);
const DOCUMENT_FOLDERS = Object.values(DocumentFolder);
const DOCUMENT_CATEGORY_BY_KEY = new Map(
    Object.entries(DocumentCategory).map(([key, category]) => [key, category])
);
const DOCUMENT_CATEGORY_KEYS_BY_DESCENDING_LENGTH = [
    ...DOCUMENT_CATEGORY_BY_KEY.keys(),
].sort((left, right) => right.length - left.length);

export const DOCUMENT_FOLDER_BY_CATEGORY: ReadonlyMap<
    DocumentCategory,
    DocumentFolder
> = new Map(
    DOCUMENT_CATEGORIES.map((category) => [
        category,
        DOCUMENT_FOLDER_BY_CATEGORY_VALUES[category],
    ])
);

export const DOCUMENT_CATEGORIES_BY_FOLDER: ReadonlyMap<
    DocumentFolder,
    readonly DocumentCategory[]
> = new Map(
    DOCUMENT_FOLDERS.map((folder) => [
        folder,
        DOCUMENT_CATEGORIES.filter(
            (category) =>
                DOCUMENT_FOLDER_BY_CATEGORY_VALUES[category] === folder
        ),
    ])
);

export const DOCUMENT_CATEGORY_BY_TYPE: ReadonlyMap<
    DocumentType,
    DocumentCategory
> = new Map(
    Object.entries(DocumentType).map(([key, type]) => [
        type,
        resolveDocumentTypeCategory(key),
    ])
);

export function getDocumentFolderForCategory(
    category: DocumentCategory
): DocumentFolder {
    return DOCUMENT_FOLDER_BY_CATEGORY_VALUES[category];
}

export function getDocumentCategoryForType(
    type: DocumentType
): DocumentCategory {
    const category = DOCUMENT_CATEGORY_BY_TYPE.get(type);
    if (category !== undefined) return category;

    throw new TypeError(`No document category found for type: ${type}`);
}

function resolveDocumentTypeCategory(key: string): DocumentCategory {
    const categoryKey = DOCUMENT_CATEGORY_KEYS_BY_DESCENDING_LENGTH.find(
        (candidate) => key.startsWith(candidate)
    );
    const category = categoryKey
        ? DOCUMENT_CATEGORY_BY_KEY.get(categoryKey)
        : undefined;
    if (category !== undefined) return category;

    throw new TypeError(`No document category found for type key: ${key}`);
}
